import React, { useEffect, useState } from "react";
import axios from "axios";
import { FaDownload, FaFilePdf, FaUsers, FaUser, FaSearch, FaCheckCircle, FaTimesCircle } from "react-icons/fa";
import Pagination from "../Pagination";

const BoletinFinalList = ({ 
  estudiantes, 
  salonInfo, 
  onGenerarTodos, 
  onGenerarIndividual,
  loading 
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Filtrar estudiantes por búsqueda
  const estudiantesFiltrados = estudiantes.filter(estudiante =>
    estudiante.nombre_completo.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Paginación
  const totalPages = Math.ceil(estudiantesFiltrados.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const estudiantesPaginados = estudiantesFiltrados.slice(startIndex, startIndex + itemsPerPage);

  const estudiantesConBoletin = estudiantes.filter(e => e.boletin_final_url).length;
  const porcentajeCompletado = estudiantes.length > 0 ? (estudiantesConBoletin / estudiantes.length) * 100 : 0;

  const handleGenerarTodos = () => {
    if (window.confirm(`¿Está seguro de generar todos los boletines finales del salón ${salonInfo?.nombre}? Esta operación puede tomar varios minutos.`)) {
      onGenerarTodos();
    }
  };

  // **NUEVA FUNCIÓN: Misma lógica que en BoletinList**
  const handleDownloadPDF = async (estudiante) => {
  try {
    const pdfUrl = estudiante?.boletin_final_url;
      if (!pdfUrl) {
        alert("Este boletín no tiene un archivo PDF disponible.");
        return;
      }
  
      // Descargar como blob
      const fileResponse = await axios.get(pdfUrl, { responseType: "blob" });
  
      const blob = new Blob([fileResponse.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
  
      // Construir nombre del archivo desde la URL
      const decodedUrl = decodeURIComponent(pdfUrl);
      const parts = decodedUrl.split("/");
      let rawFilename = parts[parts.length - 1];
  
      // Si no tiene .pdf, agrégalo
      if (!rawFilename.endsWith(".pdf")) {
        rawFilename += ".pdf";
      }
  
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", rawFilename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert("No se pudo descargar el boletín.");
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-6 gap-4">
        <div>
          <h3 className="text-xl font-semibold mb-2">Estudiantes - Boletines Finales</h3>
          <div className="text-sm text-gray-600">
            <span className={`inline-block w-3 h-3 rounded-full mr-2 ${porcentajeCompletado === 100 ? 'bg-green-500' : 'bg-yellow-500'}`}></span>
            Progreso: {estudiantesConBoletin}/{estudiantes.length} ({porcentajeCompletado.toFixed(1)}%)
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
          {/* Barra de búsqueda */}
          <div className="flex items-center border border-gray-300 rounded-full px-4 py-2 bg-gray-50 min-w-0 lg:min-w-[300px]">
            <FaSearch className="mr-2 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar estudiante..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="outline-none bg-transparent w-full"
            />
          </div>

          {/* Botón generar todos */}
          <button
            onClick={handleGenerarTodos}
            disabled={loading || estudiantes.length === 0}
            className="flex items-center justify-center px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
          >
            <FaUsers className="mr-2" />
            {loading ? "Generando..." : "Generar Todos"}
          </button>
        </div>
      </div>

      {estudiantes.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-gray-500 text-lg">No hay estudiantes en este salón.</p>
        </div>
      ) : (
        <>
          {/* Tabla de estudiantes */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="border border-gray-300 px-4 py-3 text-left font-semibold">#</th>
                  <th className="border border-gray-300 px-4 py-3 text-left font-semibold">Estudiante</th>
                  <th className="border border-gray-300 px-4 py-3 text-center font-semibold">Promedio General</th>
                  {/* <th className="border border-gray-300 px-4 py-3 text-center font-semibold">Puesto</th> */}
                  <th className="border border-gray-300 px-4 py-3 text-center font-semibold">Estado</th>
                  <th className="border border-gray-300 px-4 py-3 text-center font-semibold">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {estudiantesPaginados.map((estudiante, index) => (
                  <tr key={estudiante.users_id || estudiante.user_id} className="hover:bg-gray-50">
                    <td className="border border-gray-300 px-4 py-3 text-center font-medium">
                      {startIndex + index + 1}
                    </td>
                    <td className="border border-gray-300 px-4 py-3">
                      <div className="font-medium">{estudiante.nombre_completo}</div>
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-center">
                      {estudiante.promedio_general ? (
                        <span className={`font-bold text-lg ${
                          estudiante.promedio_general >= 4.6 ? 'text-green-600' :
                          estudiante.promedio_general >= 4.0 ? 'text-blue-600' :
                          estudiante.promedio_general >= 3.0 ? 'text-yellow-600' :
                          'text-red-600'
                        }`}>
                          {parseFloat(estudiante.promedio_general).toFixed(2)}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    {/* <td className="border border-gray-300 px-4 py-3 text-center">
                      {estudiante.puesto_grupo ? (
                        <span className="font-bold text-lg text-purple-600">
                          #{estudiante.puesto_grupo}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td> */}
                    <td className="border border-gray-300 px-4 py-3 text-center">
                      {estudiante.boletin_final_url ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                          <FaCheckCircle className="mr-1" />
                          Generado
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-600">
                          <FaTimesCircle className="mr-1" />
                          Pendiente
                        </span>
                      )}
                    </td>
                    <td className="border border-gray-300 py-3 text-center">
                      <div className="flex justify-center gap-2">
                        {/* Botón generar/regenerar */}
                        <button
                          onClick={() => onGenerarIndividual(estudiante.users_id || estudiante.user_id)}
                          disabled={loading}
                          className="flex items-center px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                          title={estudiante.boletin_final_url ? "Regenerar boletín" : "Generar boletín"}
                        >
                          <FaFilePdf className="md:mr-1" />
                          <span className="hidden md:inline">
                            {estudiante.boletin_final_url ? "Regenerar" : "Generar"}
                          </span>
                        </button>

                        {/* Botón descargar */}
                        {estudiante.boletin_final_url && (
                          <button
                            onClick={() => handleDownloadPDF(estudiante)}
                            disabled={loading}
                            className="flex items-center px-3 py-1 bg-green text-white rounded hover:bg-green-700 disabled:bg-gray-400 transition-colors"
                            title="Descargar PDF"
                          >
                            <FaDownload className="md:mr-1" />
                            <span className="hidden md:inline">Descargar</span>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Paginación */}
          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          )}

          {/* Resumen inferior */}
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-blue-600">{estudiantes.length}</div>
                <div className="text-sm text-gray-600">Total Estudiantes</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-green-600">{estudiantesConBoletin}</div>
                <div className="text-sm text-gray-600">Boletines Generados</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-orange-600">{estudiantes.length - estudiantesConBoletin}</div>
                <div className="text-sm text-gray-600">Pendientes</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-purple-600">{porcentajeCompletado.toFixed(1)}%</div>
                <div className="text-sm text-gray-600">Progreso</div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default BoletinFinalList;